import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Calendar, Fuel, Gauge, Settings2, Wrench } from 'lucide-react';
import { formatPrice, STATUS_BADGE, altForVehicle } from '../mock';
import WatchButton from './WatchButton';

export default function VehicleCard({ v, compact = false }) {
  const isSold = v.sold || v.status === 'sold';
  const isComingSoon = v.status === 'coming_soon';
  const statusKey = v.status && v.status !== 'available' ? v.status : null;
  const badge = statusKey ? STATUS_BADGE[statusKey] : null;
  return (
    <Link to={`/vehicle/${v.id}`} className="vehicle-card group block bg-white rounded-2xl border border-black/10 overflow-hidden">
      <div className="relative overflow-hidden aspect-[4/3] bg-black">
        <img
          src={v.images[0]}
          alt={altForVehicle(v)}
          className={`vehicle-img absolute inset-0 w-full h-full object-cover ${isSold ? 'grayscale-[60%] opacity-80' : ''}`}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/0 to-transparent" />

        {/* SOLD watermark — diagonal stripe across the image */}
        {isSold && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="bg-[#e11d2c] text-white font-display font-black text-[58px] sm:text-[64px] tracking-[0.25em] px-12 py-3 -rotate-[14deg] shadow-2xl border-4 border-white/95" style={{ WebkitTextStroke: '0', letterSpacing: '0.18em' }}>
              SOLD
            </div>
          </div>
        )}

        {/* Top badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {v.featured && !isSold && (
            <span className="bg-[#e11d2c] text-white text-[10px] tracking-[0.18em] uppercase font-bold px-2.5 py-1 rounded-full">Featured</span>
          )}
          <span className="bg-white/95 text-black text-[10px] tracking-[0.16em] uppercase font-semibold px-2.5 py-1 rounded-full">{v.body}</span>
        </div>
        {badge && (
          <div className="absolute top-3 right-3">
            <span className={`${badge.cls} text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1 rounded-full`} data-testid={`badge-${statusKey}`}>{badge.label}</span>
          </div>
        )}
        <div className={`absolute right-3 ${badge ? 'top-12' : 'top-3'}`}>
          <WatchButton type="vehicle" id={v.id} />
        </div>

        {/* CTA hover button */}
        <div className="absolute right-3 bottom-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <div className="bg-[#e11d2c] text-white px-3.5 py-2 rounded-full text-[11px] font-semibold inline-flex items-center gap-1.5">
            View details <ArrowUpRight size={13} />
          </div>
        </div>

        <div className="absolute left-3 bottom-3 text-white">
          <div className="text-[10px] tracking-[0.18em] uppercase font-semibold opacity-85">{v.year} · {v.make}</div>
          <div className="font-display text-[20px] font-bold leading-tight drop-shadow">{v.model}</div>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-black/50">Stock #{v.stock}</div>
            <div className="font-display text-[18px] font-bold text-black mt-0.5">{v.year} {v.make} {v.model}</div>
          </div>
          <div className="text-right">
            {isComingSoon ? (
              <div className="font-display text-[15px] font-bold text-[#e11d2c] leading-tight uppercase tracking-wider">Coming soon</div>
            ) : isSold ? (
              <div className="font-display text-[13px] font-bold text-black/70 leading-tight uppercase tracking-wider">Inquire about<br/>special import</div>
            ) : v.status === 'on_sale' && v.sale_price ? (
              <>
                <div className="text-[11px] text-black/40 line-through leading-none">{formatPrice(v.price)}</div>
                <div className="font-display text-[20px] font-bold text-[#e11d2c] leading-none mt-0.5">{formatPrice(v.sale_price)}</div>
              </>
            ) : (
              <div className="font-display text-[20px] font-bold text-[#e11d2c] leading-none">{formatPrice(v.price)}</div>
            )}
          </div>
        </div>

        {!compact && (
          <div className="grid grid-cols-4 gap-2 pt-4 border-t border-black/10">
            <Spec icon={<Calendar size={13} />} label={v.year} />
            <Spec icon={<Gauge size={13} />} label={`${Math.round(v.mileage/1000)}k km`} />
            <Spec icon={<Fuel size={13} />} label={v.fuel} />
            <Spec icon={<Settings2 size={13} />} label={v.transmission} />
          </div>
        )}

        {Number(v.parts_count || 0) > 0 && (
          <div className="mt-3 -mx-5 -mb-5 px-5 py-2.5 bg-black text-white text-[11px] tracking-wider uppercase font-bold flex items-center justify-between" data-testid={`parts-badge-${v.id}`}>
            <span className="inline-flex items-center gap-2"><Wrench size={12} className="text-[#e11d2c]" /> {v.parts_count} service part{v.parts_count === 1 ? '' : 's'} available</span>
            <span className="text-[#e11d2c]">View →</span>
          </div>
        )}
      </div>
    </Link>
  );
}

function Spec({ icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-black/65">
      <span className="text-[#e11d2c]">{icon}</span>
      <span className="truncate">{label}</span>
    </div>
  );
}
